import * as THREE from 'three';

export type MaterialKind = 'wood' | 'brick' | 'metal';

interface Pickup {
  mesh: THREE.Mesh;
  kind: MaterialKind;
  amount: number;
  bobT: number;
  baseY: number;
  collected: boolean;
}

const MAT_COLOR: Record<MaterialKind, number> = {
  wood: 0xb5651d,
  brick: 0x999999,
  metal: 0x4488cc,
};

const MAT_ICON_COLOR: Record<MaterialKind, string> = {
  wood: '#b5651d',
  brick: '#999',
  metal: '#4488cc',
};

const MAT_LABEL: Record<MaterialKind, string> = {
  wood: 'Wood',
  brick: 'Brick',
  metal: 'Metal',
};

export class FpsMaterials {
  private wood = 0;
  private brick = 0;
  private metal = 0;

  private hudEl!: HTMLDivElement;
  private woodEl!: HTMLSpanElement;
  private brickEl!: HTMLSpanElement;
  private metalEl!: HTMLSpanElement;

  private pickups: Pickup[] = [];
  private scene: THREE.Scene | null = null;

  constructor(private mount: HTMLElement) {
    this.createHud();
  }

  private createHud(): void {
    this.hudEl = document.createElement('div');
    this.hudEl.style.cssText =
      'position:fixed;bottom:90px;right:16px;z-index:36;display:flex;flex-direction:column;gap:4px;pointer-events:none;';

    for (const kind of ['wood', 'brick', 'metal'] as MaterialKind[]) {
      const row = document.createElement('div');
      row.style.cssText =
        'display:flex;align-items:center;gap:6px;background:rgba(10,14,24,0.80);border:1px solid rgba(255,255,255,0.12);border-radius:6px;padding:4px 8px;';

      const icon = document.createElement('div');
      icon.style.cssText = `width:12px;height:12px;border-radius:2px;background:${MAT_ICON_COLOR[kind]};flex-shrink:0;`;

      const label = document.createElement('span');
      label.style.cssText = 'font:600 11px system-ui,sans-serif;color:rgba(220,230,255,0.7);min-width:32px;';
      label.textContent = MAT_LABEL[kind];

      const count = document.createElement('span');
      count.style.cssText = 'font:700 12px system-ui,sans-serif;color:#e8e8ff;min-width:28px;text-align:right;';
      count.textContent = '0';

      row.appendChild(icon);
      row.appendChild(label);
      row.appendChild(count);
      this.hudEl.appendChild(row);

      if (kind === 'wood') this.woodEl = count;
      else if (kind === 'brick') this.brickEl = count;
      else this.metalEl = count;
    }

    this.mount.appendChild(this.hudEl);
  }

  private refreshHud(): void {
    this.woodEl.textContent = String(this.wood);
    this.brickEl.textContent = String(this.brick);
    this.metalEl.textContent = String(this.metal);
  }

  add(kind: MaterialKind, amount: number): void {
    if (kind === 'wood') this.wood += amount;
    else if (kind === 'brick') this.brick += amount;
    else this.metal += amount;
    this.refreshHud();
  }

  spend(kind: MaterialKind, amount: number): boolean {
    if (this.get(kind) < amount) return false;
    if (kind === 'wood') this.wood -= amount;
    else if (kind === 'brick') this.brick -= amount;
    else this.metal -= amount;
    this.refreshHud();
    return true;
  }

  get(kind: MaterialKind): number {
    if (kind === 'wood') return this.wood;
    if (kind === 'brick') return this.brick;
    return this.metal;
  }

  getTotal(): number {
    return this.wood + this.brick + this.metal;
  }

  spawnPickup(scene: THREE.Scene, x: number, y: number, z: number, kind: MaterialKind, amount: number): void {
    this.scene = scene;
    const geo = new THREE.BoxGeometry(0.3, 0.3, 0.3);
    const mat = new THREE.MeshBasicMaterial({ color: MAT_COLOR[kind] });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(x, y, z);
    scene.add(mesh);
    this.pickups.push({ mesh, kind, amount, bobT: Math.random() * Math.PI * 2, baseY: y, collected: false });
  }

  update(dt: number, playerX: number, playerY: number, playerZ: number): void {
    const MAGNET_DIST = 3;
    const toRemove: number[] = [];

    for (let i = 0; i < this.pickups.length; i++) {
      const p = this.pickups[i]!;
      if (p.collected) {
        toRemove.push(i);
        continue;
      }

      p.bobT += dt * 2;
      const dx = playerX - p.mesh.position.x;
      const dy = playerY - p.mesh.position.y;
      const dz = playerZ - p.mesh.position.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

      if (dist < 0.5) {
        // collect
        this.add(p.kind, p.amount);
        p.mesh.removeFromParent();
        (p.mesh.geometry as THREE.BufferGeometry).dispose();
        p.collected = true;
        toRemove.push(i);
      } else if (dist < MAGNET_DIST) {
        // lerp toward player
        p.mesh.position.x += dx * 0.1;
        p.mesh.position.y += dy * 0.1;
        p.mesh.position.z += dz * 0.1;
      } else {
        // bob
        p.mesh.position.y = p.baseY + Math.sin(p.bobT) * 0.1;
      }
    }

    // remove collected in reverse order
    for (let i = toRemove.length - 1; i >= 0; i--) {
      this.pickups.splice(toRemove[i]!, 1);
    }
  }

  dispose(): void {
    for (const p of this.pickups) {
      p.mesh.removeFromParent();
      (p.mesh.geometry as THREE.BufferGeometry).dispose();
    }
    this.pickups = [];
    this.hudEl.remove();
  }
}
